import { useState, Fragment } from 'react';
import { apiPatch } from '../../api/client';
import { type ConfigSaveResponse, saveResultMessage } from '../../lib/applyResult';
import { featureCategoryForId, featureCategoryLabel } from '../../lib/definitions/settings';
import { splitPath, hasNestedValue, setNestedValue, getNestedValue, fieldDomId } from './schema';
import type { SchemaField } from './schema';
import { FieldEditor } from './FieldEditor';


export function EditableSection({
  name,
  fields,
  highlightPath,
}: {
  name: string;
  fields: SchemaField[];
  highlightPath: string | null;
}) {
  const [edits, setEdits] = useState<Record<string, unknown>>({});
  const [savedValues, setSavedValues] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const apiSection = splitPath(name).section;

  const dirtyKeys = Object.keys(edits);

  async function onSave() {
    if (dirtyKeys.length === 0) {
      setSaveMsg({ kind: "error", text: "No changes to save." });
      return;
    }
    setSaving(true);
    setSaveMsg(null);
    try {
      const resp = await apiPatch<ConfigSaveResponse>(`/api/config/${encodeURIComponent(apiSection)}`, { patch: edits });
      setSaveMsg(saveResultMessage(resp?.applied));
      setSavedValues((previous) => {
        let next = previous;
        for (const field of fields) {
          const path = field.path.startsWith(`${apiSection}.`) ? field.path.slice(apiSection.length + 1) : splitPath(field.path).key;
          if (hasNestedValue(edits, path)) next = setNestedValue(next, path, getNestedValue(edits, path));
        }
        return next;
      });
      setEdits({});
    } catch (e) {
      setSaveMsg({ kind: "error", text: e instanceof Error ? e.message : "Save failed." });
    } finally {
      setSaving(false);
    }
  }

  // Feature flags tab (2026-09-27 first-installer review, part5d, item 2):
  // group the ~30 features.* fields by category, rather than one flat
  // grid — schema fields are already sorted by category (config_schema.py's
  // FEATURES_FIELDS), so a heading is inserted whenever the category of
  // the current field differs from the previous one. Every other section
  // (name !== "features") renders exactly as before — no category concept
  // applies to them.
  let previousFeatureCategory: string | undefined;

  return (
    <div className="settings-pane">
      <fieldset disabled={saving} className="settings-fields-grid settings-fields-disabled">
        {fields.map((f, index) => {
          const key = f.path.startsWith(`${apiSection}.`) ? f.path.slice(apiSection.length + 1) : splitPath(f.path).key;
          const current = hasNestedValue(edits, key)
            ? getNestedValue(edits, key)
            : hasNestedValue(savedValues, key)
              ? getNestedValue(savedValues, key)
              : (f.value ?? f.default);
          let categoryHeading: string | null = null;
          if (name === "features") {
            const category = featureCategoryForId(key);
            if (category !== previousFeatureCategory) {
              categoryHeading = category ? featureCategoryLabel(category) : "Other";
              previousFeatureCategory = category;
            }
          }
          const isFirstHeading = categoryHeading != null && index === 0;
          return (
            <Fragment key={f.path}>
              {categoryHeading && (
                <div
                  className="settings-field-span-full radio-snapshot-subsection"
                  style={isFirstHeading ? { border: "none", marginTop: 0, paddingTop: 0 } : undefined}
                >
                  <h5>{categoryHeading}</h5>
                </div>
              )}
              <div id={fieldDomId(name, f.path)}>
                <FieldEditor
                  field={f}
                  value={current}
                  dirty={hasNestedValue(edits, key)}
                  highlighted={highlightPath === f.path}
                  onChange={(v) => setEdits((prev) => setNestedValue(prev, key, v))}
                />
              </div>
            </Fragment>
          );
        })}
      </fieldset>
      <div className="settings-pane-actions">
        {saveMsg && (
          <span className="settings-save-status" data-kind={saveMsg.kind}>
            {saveMsg.text}
          </span>
        )}
        <button className="settings-save-btn" onClick={onSave} disabled={saving || dirtyKeys.length === 0}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
